import { useAuth0 } from '@auth0/auth0-react';
import { store } from 'components/Store';
import parliamentarianUsers, { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import SimpleSearchBar from './SimpleSearchBar';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnalyticsPopup, ReportsPopup } from './NotFeaturePopups';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import NavPagesLoggedOut from './NavPagesLoggedOut';
import { Portal } from 'react-portal';
import getAppSite from 'utils/getAppSite';
import useNavSections from 'components/Dashboard/Navigation/useNavSections';

const NavPages = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { activePlan, discardChangesShown } = state;
  const { code } = activePlan ?? {};

  const closeMenu = () => {
    const hamburgerMenu = document.getElementsByClassName('hamburger')[0];
    hamburgerMenu?.classList.remove('is-active');
    dispatch({ type: 'SHOW_HIDE_MENU', value: true });
  };

  const { isAuthenticated } = useAuth0();
  const dataHubUser = dataHubUsers(activePlan);
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [showAnalyticsPopup, setShowAnalyticsPopup] = useState(false);
  const [isOpenReportsPopup, setIsOpenReportsPopup] = useState(false);
  const isMobile = useIsMobile();
  const { newAgencyPlan } = useTopicOrClientWord();
  const navPagesRef = useRef(null);
  const actionsElement = useRef(null);
  const linksContainer = useRef(null);

  const { top, height, left } = actionsElement.current?.getBoundingClientRect() ?? {};

  const closeMoreMenu = useCallback(() => setIsOpen(false), []);

  const { linksToRender, moreLinks, urls } = useNavSections({
    linksContainerRef: linksContainer,
    navPagesRef,
    actionsElementRef: actionsElement,
    onShowAnalyticsPopup: () => setShowAnalyticsPopup(true),
    onShowReportsPopup: () => setIsOpenReportsPopup(true),
  });

  const moreIsHighlighted = urls.some((item) => {
    if (item.startsWith(location.pathname) || location.pathname.startsWith(item)) {
      return true;
    }
    if (item.startsWith('/analytics/dashboard') && /^\/analytics\/[\w-]+/.test(location.pathname)) {
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (discardChangesShown && isOpen) {
      setIsOpen(false);
      dispatch({ type: 'MODIFY_SECTION', parameter: 'discardChangesShown', value: false });
    }
  }, [discardChangesShown]);

  return (
    <>
      <ul
        className={`position-relative policy-list d-flex flex-lg-row flex-column mb-0 pl-0 nav-pages ${
          parliamentarianUsers(code) ? 'nav-pages-additional-links' : ''
        } pt-lg-0 ${isAuthenticated ? 'pt-3 flex-lg-grow-1' : ''} ${newAgencyPlan ? 'flex-grow-1 px-lg-2' : ''} ${
          getAppSite() === 'usa' ? 'nav-pages-usa' : ''
        }`}
        ref={navPagesRef}
      >
        {isAuthenticated && Object.keys(activePlan).length > 0 && (
          <>
            {isMobile && (
              <li className='search-nav-button-container py-3 mb-2'>
                <div className='home-searchbar-container border-grey border rounded-button sidenav-searchbar'>
                  <SimpleSearchBar
                    homePage
                    enforceShortPlaceholder
                    closeFunction={() => {
                      closeMenu();
                    }}
                  />
                </div>
              </li>
            )}
            <div
              className={`d-lg-flex flex-lg-wrap overflow-hidden ${dataHubUser && !isMobile ? '' : 'nav-pages-links-container'} row-gap-10`}
              ref={linksContainer}
              style={{ maxHeight: '24px' }}
            >
              <NavLinks linksToRender={linksToRender} />
            </div>
            {moreLinks?.length > 0 && !isMobile && (
              <div ref={actionsElement} className={`nav-pages-more-btn`}>
                <button
                  className={`general-button px-3 more-navigation-element ${moreIsHighlighted ? 'active-page' : ''}`}
                  style={{ height: '24px' }}
                  onClick={() => {
                    setIsOpen(!isOpen);
                  }}
                >
                  <span className={`dropdown-item-element dropdown-item-element-10 ${isOpen ? 'open' : ''}`}>More</span>
                </button>
                {isOpen && (
                  <>
                    <Portal node={document && document.querySelector('body')}>
                      <div
                        className='general-close-background adjustment-of-position'
                        onClick={() => setIsOpen(false)}
                      ></div>
                      <div
                        className='box-shadow rounded py-2 adjustment-of-position'
                        style={{ position: 'fixed', top: `${top + height + 10}px`, left: `${left}px` }}
                      >
                        {moreLinks?.map((link) => {
                          const { linkProps, className, label, disabled, onClick, key } = link;
                          return (
                            <Link
                              {...linkProps}
                              className={className}
                              key={key}
                              onClick={(e) => {
                                onClick?.(e);
                                closeMoreMenu();
                              }}
                            >
                              {label}
                              {disabled && <span className='icon-padlock ml-2 paragraph-p3' />}
                            </Link>
                          );
                        })}
                      </div>
                    </Portal>
                  </>
                )}
              </div>
            )}
          </>
        )}
        {!isAuthenticated && <NavPagesLoggedOut closeMenu={closeMenu} />}
      </ul>
      <AnalyticsPopup isOpen={showAnalyticsPopup} setIsOpen={setShowAnalyticsPopup} />
      <ReportsPopup isOpen={isOpenReportsPopup} setIsOpen={setIsOpenReportsPopup} />
    </>
  );
};

const NavLinks = (props) => {
  const { linksToRender } = props;
  return (
    <>
      {linksToRender?.map((link) => {
        const { condition, label, listClassname, disabled, ...navLinkProps } = link;
        if (condition) {
          return (
            <li className={listClassname} key={`item-${label}`}>
              <NavLink {...navLinkProps}>
                {label}
                {disabled && <span className='icon-padlock ml-2 paragraph-p3' />}
              </NavLink>
            </li>
          );
        } else {
          return null;
        }
      })}
    </>
  );
};

export { default as useResearchItemsUrl } from 'components/Dashboard/Navigation/useResearchItemsUrl';
export default NavPages;
